import React, { useState, useEffect } from "react"
import { Row, Col, Card, CardBody, Button } from "reactstrap"
import Select from "react-select"
import api from "../../services/api"
import AuthService from "../../services/Auth.service"
import CondominioSelect, {
  empresasToSelectOptions,
} from "../../components/Empresa/CondominioSelect"
import Swal from "sweetalert2"
import ReactApexChart from "react-apexcharts"
import moment from "moment"
import DatePicker, { registerLocale } from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import ptBR from "date-fns/locale/pt-BR"

registerLocale("pt-BR", ptBR)

const filterOptions = [
  { value: "range", label: "Diário (Por período)" },
  { value: "month", label: "Mensal" },
]

const SyndicDashboard = () => {
  const [filterMode, setFilterMode] = useState(filterOptions[0])
  const [condos, setCondos] = useState([])
  const [units, setUnits] = useState([])
  const [selectedCondo, setSelectedCondo] = useState(null)
  const [selectedUnit, setSelectedUnit] = useState(null)

  // Date range inputs
  const [startDate, setStartDate] = useState(
    moment().startOf("month").format("YYYY-MM-DD")
  )
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"))

  // Month range inputs
  const [startMonth, setStartMonth] = useState("")
  const [endMonth, setEndMonth] = useState("")

  const [isLoading, setIsLoading] = useState(false)

  const [rawData, setRawData] = useState([])
  const [chartSeries, setChartSeries] = useState([])
  const [chartOptions, setChartOptions] = useState({})
  const [viewMode, setViewMode] = useState("daily") // "daily", "monthly", "drilldown"
  const [hasData, setHasData] = useState(false)

  const [totalConsumption, setTotalConsumption] = useState(0)
  const [currentReading, setCurrentReading] = useState(0)

  const user = AuthService.getCurrentUser()
  const DS_STATUS = user?.user?.DS_STATUS
  const OID_EMPRESA = user?.empresa?.[0]?.OID_EMPRESA

  const abortControllerRef = React.useRef(null)

  useEffect(() => {
    fetchCondos()
    const onSessionUpdated = event => {
      if (Number(DS_STATUS) === 10) return
      const list = empresasToSelectOptions(
        AuthService.getLinkedEmpresas(event.detail),
      )
      setCondos(list)
      setSelectedCondo(current => {
        if (current && list.some(item => String(item.value) === String(current.value))) {
          return current
        }
        return list[0] || null
      })
    }
    window.addEventListener("syskeeper:session-updated", onSessionUpdated)

    return () => {
      window.removeEventListener("syskeeper:session-updated", onSessionUpdated)
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const fetchCondos = async () => {
    try {
      if (Number(DS_STATUS) === 10) {
        const response = await api.get("/empresa/")
        const condoList = empresasToSelectOptions(response.data)
        setCondos(condoList)
        setSelectedCondo(condoList[0] || null)
        fetchUnits("")
        return
      }

      const session = await AuthService.refreshEmpresas()
      const condoList = empresasToSelectOptions(
        AuthService.getLinkedEmpresas(session),
      )
      setCondos(condoList)
      setSelectedCondo(condoList[0] || null)
      fetchUnits("")
    } catch (error) {
      console.error(error)
    }
  }

  const fetchUnits = async empresaId => {
    try {
      // Iniciar lista com opção "Todos"
      let unitList = [{ value: "", label: "Todos" }]

      if (!empresaId) {
        // Fetch all units for user if no condo selected (Todos)
        if (user?.user?.OID_USUARIO) {
          const response = await api.get(
            `/imovel/hidro/${user.user.OID_USUARIO}`
          )
          unitList = [
            ...unitList,
            ...response.data.map(u => ({
              value: u.OID_MEDIDOR,
              label: `Medidor: ${u.NR_MEDIDOR} - ${u.imovel}`,
            })),
          ]
        }
        setUnits(unitList)
        setSelectedUnit(unitList[0])
        return
      }

      const response = await api.get(`/imovel/empresa/${empresaId}`)
      unitList = [
        ...unitList,
        ...response.data.map(u => ({
          value: u.OID_MEDIDOR,
          label: `Medidor: ${u.NR_MEDIDOR} - ${u.imovel}`,
        })),
      ]
      setUnits(unitList)
      setSelectedUnit(unitList[0])
    } catch (error) {
      console.error(error)
    }
  }

  const handleCondoChange = selected => {
    setSelectedCondo(selected)
    setSelectedUnit(null)
    setUnits([])
    if (selected) {
      fetchUnits(selected.value)
    }
  }

  const isFullMonthRange = (start, end, isDefault) => {
    if (isDefault) return true // Default 3 meses é sempre considerado full month
    if (!start || !end) return false
    const s = moment(start)
    const e = moment(end)
    return (
      s.date() === 1 &&
      e.format("YYYY-MM-DD") === e.clone().endOf("month").format("YYYY-MM-DD")
    )
  }

  const handleSearch = async () => {
    if (!selectedCondo) {
      Swal.fire({ icon: "warning", text: "Selecione um Condomínio." })
      return
    }

    let start = ""
    let end = ""

    // Logic for determining start/end dates
    if (filterMode.value === "month") {
      if (!startMonth && !endMonth) {
        // Default to last 3 months
        end = moment().endOf("month").format("YYYY-MM-DD")
        start = moment()
          .subtract(2, "months")
          .startOf("month")
          .format("YYYY-MM-DD")
      } else {
        // Use selected months

        let s = startMonth ? moment(startMonth) : moment()
        let e = endMonth ? moment(endMonth) : moment()

        if (startMonth && !endMonth) e = moment(startMonth)
        if (!startMonth && endMonth) s = moment(endMonth)

        start = s.startOf("month").format("YYYY-MM-DD")
        end = e.endOf("month").format("YYYY-MM-DD")
      }
    } else {
      // Daily / Range mode
      if (!startDate && !endDate) {
        // Default current month
        end = moment().format("YYYY-MM-DD")
        start = moment().startOf("month").format("YYYY-MM-DD")
      } else {
        start = startDate
        end = endDate
      }
    }

    const diffDays = moment(end).diff(moment(start), "days")

    // Strict limit for daily view (range)
    if (filterMode.value === "range" && diffDays > 100) {
      Swal.fire({
        icon: "warning",
        text: "No modo diário, o intervalo máximo é de 100 dias.",
      })
      return
    }

    if (filterMode.value === "month" && diffDays > 750) {
      Swal.fire({
        icon: "warning",
        text: "O intervalo máximo é de 2 anos.",
      })
      return
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()

    setIsLoading(true)
    setHasData(false)

    try {
      const body = {
        OID_CONDOMINIO: selectedCondo.value,
        OID_MEDIDOR: selectedUnit ? selectedUnit.value : "",
        DT_FILTER_INI: start,
        DT_FILTER_FIM: end,
      }

      const response = await api.post("/consumptions/registro", body, {
        signal: abortControllerRef.current.signal,
      })
      if (response.data && response.data.length > 0) {
        // Group by unit to calculate diff correctly when multiple units are returned
        const groupedData = {}
        response.data.forEach(item => {
          const key = `${item.NR_HIDROMETRO_IMOVEL}-${item.DS_COMPLEMENTO_IMOVEL}`
          if (!groupedData[key]) groupedData[key] = []
          groupedData[key].push(item)
        })

        let dataWithDiff = []
        let totalCurrentReading = 0

        Object.values(groupedData).forEach(group => {
          // Sort each group chronologically descending
          group.sort((a, b) => new Date(b.DT_LEITURA) - new Date(a.DT_LEITURA))

          // Calculate diff
          const groupWithDiff = group.map((item, i) => {
            const nextItem = group[i + 1]
            const diff = nextItem
              ? Math.trunc(item.NR_PULSO) - Math.trunc(nextItem.NR_PULSO)
              : 0
            return {
              ...item,
              NR_DIFERENCA_CONSUMO: diff > 0 ? diff : 0,
            }
          })

          dataWithDiff.push(...groupWithDiff)

          // Accumulate Leitura Atual (Latest of this unit)
          const latest = groupWithDiff[0]
          const reloj = parseInt(latest.RELOJOARIA || "0")
          const pulsoLitros =
            latest.VR_LEITURA_LITROS * latest.VR_LITROS_POR_PULSO
          totalCurrentReading += reloj + pulsoLitros
        })

        // Re-sort the combined array
        dataWithDiff.sort(
          (a, b) => new Date(b.DT_LEITURA) - new Date(a.DT_LEITURA)
        )

        // Filter out readings that are strictly outside the requested date range
        const filteredForDisplay = dataWithDiff.filter(item => {
          let itemDateStr = ""
          // Ensure we handle both ISO strings and custom date strings correctly
          if (
            typeof item.DT_LEITURA === "string" &&
            item.DT_LEITURA.includes("T")
          ) {
            // If it's ISO, just grab the YYYY-MM-DD part directly to avoid any TZ shifts
            itemDateStr = item.DT_LEITURA.split("T")[0]
          } else {
            itemDateStr = moment(item.DT_LEITURA).format("YYYY-MM-DD")
          }

          return itemDateStr >= start && itemDateStr <= end
        })

        setRawData(filteredForDisplay)
        setCurrentReading(totalCurrentReading.toString().padStart(7, "0"))

        if (filterMode.value === "month") {
          setViewMode("monthly")
          renderMonthlyChart(filteredForDisplay)
        } else {
          setViewMode("daily")
          renderDailyChart(filteredForDisplay)
        }
        setHasData(true)
      } else {
        Swal.fire({
          icon: "info",
          text: "Nenhum dado encontrado para o período.",
        })
      }
    } catch (error) {
      Swal.fire({ icon: "error", text: "Erro ao buscar dados." })
    } finally {
      setIsLoading(false)
    }
  }

  const calculateTotal = data => {
    return data.reduce(
      (acc, curr) => acc + curr.NR_DIFERENCA_CONSUMO * curr.VR_LITROS_POR_PULSO,
      0
    )
  }

  const renderMonthlyChart = data => {
    const total = calculateTotal(data)
    setTotalConsumption(total)

    const agg = {}
    data.forEach(item => {
      let m = ""
      if (
        typeof item.DT_LEITURA === "string" &&
        item.DT_LEITURA.includes("T")
      ) {
        const parts = item.DT_LEITURA.split("T")[0].split("-")
        m = `${parts[0]}-${parts[1]}`
      } else {
        m = moment(item.DT_LEITURA).format("YYYY-MM")
      }
      if (!agg[m]) agg[m] = 0
      agg[m] += item.NR_DIFERENCA_CONSUMO * item.VR_LITROS_POR_PULSO
    })

    const categories = Object.keys(agg).sort()
    const seriesData = categories.map(k => agg[k] / 1000) // m³
    const formattedCategories = categories.map(k =>
      moment(k, "YYYY-MM").format("MMM/YYYY")
    )

    setChartSeries([{ name: "Consumo Mensal (m³)", data: seriesData }])
    setChartOptions(
      getChartConfig(
        formattedCategories,
        seriesData,
        total / 1000,
        "monthly",
        categories,
        data
      )
    )
  }

  const renderDailyChart = (data, monthFilter = null) => {
    let filteredData = data
    if (monthFilter) {
      filteredData = data.filter(
        item => moment(item.DT_LEITURA).format("YYYY-MM") === monthFilter
      )
    }

    const total = calculateTotal(filteredData)
    // Always update total consumption regardless of drilldown
    setTotalConsumption(total)

    const agg = {}
    filteredData.forEach(item => {
      // Extract YYYY-MM-DD safely, ignoring timezone shifts
      let d = ""
      if (
        typeof item.DT_LEITURA === "string" &&
        item.DT_LEITURA.includes("T")
      ) {
        d = item.DT_LEITURA.split("T")[0]
      } else {
        d = moment(item.DT_LEITURA).format("YYYY-MM-DD")
      }
      if (!agg[d]) agg[d] = 0
      agg[d] += item.NR_DIFERENCA_CONSUMO * item.VR_LITROS_POR_PULSO
    })

    const categories = Object.keys(agg).sort()
    const seriesData = categories.map(k => agg[k] / 1000) // m³
    const formattedCategories = categories.map(k => {
      const [y, m, d] = k.split("-")
      return `${d}/${m}`
    })

    setChartSeries([{ name: "Consumo Diário (m³)", data: seriesData }])
    setChartOptions(
      getChartConfig(
        formattedCategories,
        seriesData,
        total / 1000,
        "daily",
        [],
        data
      )
    )
  }

  const getChartConfig = (
    categories,
    data,
    totalPeriod,
    mode,
    rawCategories = [],
    fullData = []
  ) => {
    return {
      chart: {
        type: "bar",
        height: 350,
        events: {
          dataPointSelection: (event, chartContext, config) => {
            if (mode === "monthly" && rawCategories.length > 0) {
              const index = config.dataPointIndex
              if (index !== undefined && index !== -1) {
                const selectedMonth = rawCategories[index]
                setViewMode("drilldown")
                renderDailyChart(fullData, selectedMonth)
              }
            }
          },
          click: (event, chartContext, config) => {
            if (mode === "monthly" && rawCategories.length > 0) {
              const index = config.dataPointIndex
              if (index !== undefined && index !== -1) {
                const selectedMonth = rawCategories[index]
                setViewMode("drilldown")
                renderDailyChart(fullData, selectedMonth)
              }
            }
          },
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          horizontal: false,
          columnWidth: "50%",
        },
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        categories: categories,
      },
      tooltip: {
        enabled: true,
        shared: false,
        intersect: false,
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          const val = series[seriesIndex][dataPointIndex]
          const perc =
            totalPeriod > 0 ? ((val / totalPeriod) * 100).toFixed(2) : 0
          return `
            <div style="padding: 10px; background: white; border: 1px solid #ccc; border-radius: 5px; color: black; box-shadow: 0px 2px 4px rgba(0,0,0,0.1);">
              <strong style="display:block; margin-bottom:5px; border-bottom: 1px solid #eee; padding-bottom: 3px;">${
                categories[dataPointIndex]
              }</strong>
              Consumo: <b>${val.toFixed(2)} m³</b><br/>
              Porcentagem: <b>${perc}%</b>
            </div>
          `
        },
      },
    }
  }

  const goBackToMonthly = () => {
    setViewMode("monthly")
    renderMonthlyChart(rawData)
  }

  return (
    <Card>
      <CardBody>
        <h4 className="card-title mb-4">Dashboard do Síndico</h4>
        <Row className="mb-4">
          <Col md={3}>
            <label htmlFor="condominio-sindico-dashboard">Condomínio</label>
            <CondominioSelect
              inputId="condominio-sindico-dashboard"
              options={condos}
              value={selectedCondo}
              onChange={handleCondoChange}
              isDisabled={isLoading}
            />
          </Col>
          <Col md={2}>
            <label>Unidade</label>
            <Select
              options={units}
              value={selectedUnit}
              onChange={s => setSelectedUnit(s)}
              placeholder="Selecione..."
              isDisabled={isLoading}
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              menuPosition="fixed"
              maxMenuHeight={320}
              styles={{
                menuPortal: base => ({ ...base, zIndex: 9999 }),
                menuList: base => ({ ...base, maxHeight: 320, overflowY: "auto" }),
              }}
            />
          </Col>
          <Col md={2}>
            <label>Tipo de Filtro</label>
            <Select
              options={filterOptions}
              value={filterMode}
              onChange={s => {
                setFilterMode(s)
                // Reset fields to avoid confusion
                setStartDate("")
                setEndDate("")
                setStartMonth("")
                setEndMonth("")
              }}
              isDisabled={isLoading}
            />
          </Col>

          {filterMode.value === "range" ? (
            <>
              <Col md={2}>
                <label>Data Inicial</label>
                <input
                  type="date"
                  className="form-control"
                  value={startDate}
                  onChange={e => {
                    const newStartDate = e.target.value
                    // Validar se data inicial não é maior que data final
                    if (endDate && newStartDate > endDate) {
                      Swal.fire({
                        icon: "warning",
                        title: "Data inválida",
                        text: "A data inicial não pode ser maior que a data final.",
                      })
                      return
                    }
                    setStartDate(newStartDate)
                  }}
                  max={endDate || undefined}
                  disabled={isLoading}
                />
              </Col>
              <Col md={2}>
                <label>Data Final</label>
                <input
                  type="date"
                  className="form-control"
                  value={endDate}
                  onChange={e => {
                    const newEndDate = e.target.value
                    // Validar se data final não é menor que data inicial
                    if (startDate && newEndDate < startDate) {
                      Swal.fire({
                        icon: "warning",
                        title: "Data inválida",
                        text: "A data final não pode ser menor que a data inicial.",
                      })
                      return
                    }
                    setEndDate(newEndDate)
                  }}
                  min={startDate || undefined}
                  disabled={isLoading}
                />
              </Col>
            </>
          ) : (
            <>
              <Col md={2}>
                <label>Mês Inicial</label>
                <DatePicker
                  selected={startMonth ? moment(startMonth).toDate() : null}
                  onChange={date => {
                    const newStartMonth = date ? moment(date).format("YYYY-MM") : ""
                    // Validar se mês inicial não é maior que mês final
                    if (endMonth && newStartMonth > endMonth) {
                      Swal.fire({
                        icon: "warning",
                        title: "Mês inválido",
                        text: "O mês inicial não pode ser maior que o mês final.",
                      })
                      return
                    }
                    setStartMonth(newStartMonth)
                  }}
                  dateFormat="MM/yyyy"
                  showMonthYearPicker
                  locale="pt-BR"
                  maxDate={endMonth ? moment(endMonth).endOf("month").toDate() : new Date()}
                  className="form-control"
                  placeholderText="Selecione..."
                  popperPlacement="bottom-start"
                  portalId="root-portal"
                  disabled={isLoading}
                />
              </Col>
              <Col md={2}>
                <label>Mês Final</label>
                <DatePicker
                  selected={endMonth ? moment(endMonth).toDate() : null}
                  onChange={date => {
                    const newEndMonth = date ? moment(date).format("YYYY-MM") : ""
                    // Validar se mês final não é menor que mês inicial
                    if (startMonth && newEndMonth < startMonth) {
                      Swal.fire({
                        icon: "warning",
                        title: "Mês inválido",
                        text: "O mês final não pode ser menor que o mês inicial.",
                      })
                      return
                    }
                    setEndMonth(newEndMonth)
                  }}
                  dateFormat="MM/yyyy"
                  showMonthYearPicker
                  locale="pt-BR"
                  minDate={startMonth ? moment(startMonth).startOf("month").toDate() : null}
                  maxDate={new Date()}
                  className="form-control"
                  placeholderText="Selecione..."
                  popperPlacement="bottom-start"
                  portalId="root-portal"
                  disabled={isLoading}
                />
              </Col>
            </>
          )}
          <Col md={1} className="d-flex align-items-end">
            <Button
              color="primary"
              onClick={handleSearch}
              disabled={isLoading}
              block
            >
              {isLoading ? "..." : "Busca"}
            </Button>
          </Col>
        </Row>

        {isLoading && (
          <div className="text-center mt-5 mb-5">
            <i
              className="mdi mdi-spin mdi-loading"
              style={{ fontSize: "50px" }}
            ></i>
          </div>
        )}

        {!isLoading && hasData && (
          <>
            <Row className="mb-4">
              {viewMode !== "monthly" && (
                <Col md={6}>
                  <div className="p-3 bg-light rounded text-center">
                    <h5>Leitura Atual</h5>
                    {(() => {
                      const readingStr = currentReading
                        .toString()
                        .padStart(7, "0")
                      const blackPart = readingStr.slice(0, -3)
                      const redPart = readingStr.slice(-3)
                      return (
                        <div
                          style={{
                            fontSize: "28px",
                            fontWeight: "bold",
                            color: "#333",
                            marginTop: "10px",
                          }}
                        >
                          <span style={{ color: "black" }}>{blackPart}</span>
                          <span style={{ color: "red" }}>{redPart}</span>{" "}
                          <span style={{ color: "black", marginLeft: "5px" }}>
                            m³
                          </span>
                        </div>
                      )
                    })()}
                  </div>
                </Col>
              )}
              <Col md={6} className={viewMode === "monthly" ? "mx-auto" : ""}>
                <div className="p-3 bg-light rounded text-center">
                  {(() => {
                    const formatted = totalConsumption
                      .toFixed(0)
                      .padStart(7, "0")
                    const blackPart = formatted.slice(0, -3)
                    const redPart = formatted.slice(-3)
                    const unit = totalConsumption < 1000 ? "ls" : "m³"
                    return (
                      <>
                        <h5>
                          Consumo no Período {unit === "ls" ? "(litros)" : ""}
                        </h5>
                        <div
                          style={{
                            fontSize: "28px",
                            fontWeight: "bold",
                            color: "#333",
                            marginTop: "10px",
                          }}
                        >
                          <span style={{ color: "black" }}>{blackPart}</span>
                          <span style={{ color: "red" }}>{redPart}</span>{" "}
                          <span
                            style={{
                              color: unit === "ls" ? "red" : "black",
                              marginLeft: "5px",
                            }}
                          >
                            {unit}
                          </span>
                        </div>
                      </>
                    )
                  })()}
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                {viewMode === "drilldown" && (
                  <Button
                    color="secondary"
                    size="sm"
                    onClick={goBackToMonthly}
                    className="mb-3"
                  >
                    <i className="bx bx-arrow-back mr-1"></i> Voltar para Visão
                    Mensal
                  </Button>
                )}

                <div key={viewMode}>
                  <ReactApexChart
                    options={chartOptions}
                    series={chartSeries}
                    type="bar"
                    height={350}
                  />
                </div>
              </Col>
            </Row>
          </>
        )}
      </CardBody>
    </Card>
  )
}

export default SyndicDashboard
